import React, { useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { sampleEdgePoints } from '../pointCloudUtils';
import { SOLAR_DIM, SOLAR_PRIMARY, SOLAR_GLOW } from '../twinColors';

// Pre-allocated colors for useFrame lerp (avoids per-frame allocation)
const _lerpA = new THREE.Color();
const _lerpB = new THREE.Color();
const PANEL_ROWS = 2;
const PANEL_COLS = 4;
const PANEL_W = 3.0;
const PANEL_H = 0.06;
const PANEL_D = 1.8;
const GAP = 0.4;
const TILT_ANGLE = Math.PI / 7;

/**
 * SolarArray - Point cloud tilted panel array.
 * Particles glow brighter with power output. Deep blue base, brighter blue when active.
 */
export default function SolarArray({ state, position, onClick }) {
    const pointsRef = useRef();
    const glowRef = useRef();

    const powerRatio = useMemo(() => {
        if (!state?.power_kw || !state?.capacity_kw) return 0;
        return Math.min(1, Math.max(0, state.power_kw / state.capacity_kw));
    }, [state?.power_kw, state?.capacity_kw]);

    // Sample points for panel edges (dispose intermediate geometries)
    const { panelPoints, strutPoints, totalW, totalD } = useMemo(() => {
        const panelBox = new THREE.BoxGeometry(PANEL_W, PANEL_H, PANEL_D);
        const panelEdges = new THREE.EdgesGeometry(panelBox);
        const strutCyl = new THREE.CylinderGeometry(0.05, 0.05, 3, 6);
        const strutEdges = new THREE.EdgesGeometry(strutCyl);

        const tw = PANEL_COLS * (PANEL_W + GAP) - GAP;
        const td = PANEL_ROWS * (PANEL_D + GAP) - GAP;

        // Sample points for all 8 panels combined
        const allPanelPts = [];
        for (let r = 0; r < PANEL_ROWS; r++) {
            for (let c = 0; c < PANEL_COLS; c++) {
                const ox = c * (PANEL_W + GAP) - tw / 2 + PANEL_W / 2;
                const oz = r * (PANEL_D + GAP) - td / 2 + PANEL_D / 2;
                const pts = sampleEdgePoints(panelEdges, 60, 0.04);
                for (let i = 0; i < pts.length; i += 3) {
                    allPanelPts.push(pts[i] + ox, pts[i + 1], pts[i + 2] + oz);
                }
            }
        }

        // Strut points
        const allStrutPts = [];
        const xPositions = [-tw / 2 + 1.0, 0, tw / 2 - 1.0];
        const zPositions = [-td / 2 + 0.5, td / 2 - 0.5];
        for (const zp of zPositions) {
            for (const xp of xPositions) {
                const pts = sampleEdgePoints(strutEdges, 20, 0.03);
                for (let i = 0; i < pts.length; i += 3) {
                    allStrutPts.push(pts[i] + xp, pts[i + 1] + 1.5, pts[i + 2] + zp);
                }
            }
        }

        // Dispose intermediate geometries — only the Float32Arrays are needed
        panelBox.dispose();
        panelEdges.dispose();
        strutCyl.dispose();
        strutEdges.dispose();

        return {
            panelPoints: new Float32Array(allPanelPts),
            strutPoints: new Float32Array(allStrutPts),
            totalW: tw,
            totalD: td,
        };
    }, []);

    useFrame(({ clock }) => {
        if (pointsRef.current) {
            const t = clock.elapsedTime;
            const mat = pointsRef.current.material;
            // Pulse opacity with power
            mat.opacity = 0.5 + powerRatio * 0.4 + Math.sin(t * 2) * 0.05 * powerRatio;
            _lerpA.set(SOLAR_DIM);
            _lerpB.set(SOLAR_PRIMARY);
            mat.color.copy(_lerpA.lerp(_lerpB, powerRatio));
        }
        if (glowRef.current) {
            const t = clock.elapsedTime;
            glowRef.current.material.opacity = 0.1 + powerRatio * 0.25 + Math.sin(t * 2) * 0.03 * powerRatio;
        }
    });

    return (
        <group position={position} onClick={onClick}>
            {/* Strut particles */}
            <points>
                <bufferGeometry>
                    <bufferAttribute
                        attach="attributes-position"
                        count={strutPoints.length / 3}
                        array={strutPoints}
                        itemSize={3}
                    />
                </bufferGeometry>
                <pointsMaterial
                    color={SOLAR_DIM}
                    size={0.08}
                    transparent
                    opacity={0.5}
                    depthWrite={false}
                    sizeAttenuation
                    blending={THREE.AdditiveBlending}
                />
            </points>

            {/* Panel particles (tilted) */}
            <group position={[0, 3.2, 0]} rotation={[TILT_ANGLE, 0, 0]}>
                <points ref={pointsRef}>
                    <bufferGeometry>
                        <bufferAttribute
                            attach="attributes-position"
                            count={panelPoints.length / 3}
                            array={panelPoints}
                            itemSize={3}
                        />
                    </bufferGeometry>
                    <pointsMaterial
                        color={SOLAR_DIM}
                        size={0.12}
                        transparent
                        opacity={0.4}
                        depthWrite={false}
                        sizeAttenuation
                        blending={THREE.AdditiveBlending}
                    />
                </points>

                {/* Glow plane under panels when active */}
                <mesh
                    ref={glowRef}
                    position={[0, -0.08, 0]}
                    rotation={[-TILT_ANGLE, 0, 0]}
                >
                    <planeGeometry args={[totalW, totalD]} />
                    <meshBasicMaterial
                        color={SOLAR_GLOW}
                        transparent
                        opacity={0.05}
                        side={THREE.DoubleSide}
                        depthWrite={false}
                        blending={THREE.AdditiveBlending}
                    />
                </mesh>
            </group>
        </group>
    );
}
